/**
 * 배치 자동 누끼 (설계문서 §5.7).
 *
 * "그 날의 사진들"은 복수다. 5장이면 서버에서 2~5초 x 5 = 10~25초.
 * 한 장씩 기다리게 하면 기능이 죽는다.
 *
 * 비싼 것은 서버 왕복이므로 그것만 병렬로 돌리고, 합성·인코딩은 각 항목이
 * 자기 서피스에서 처리한다. 편집 세션(undo 로그, 작업 해상도)이 필요 없으므로
 * 엔진을 태우지 않고 목표 크기로 바로 합성한다.
 */
import type {
  ExportOptions, MaskBackend, Resolution, SurfaceFactory,
} from './types.js';

export interface BatchResult {
  index: number;
  ok: boolean;
  blob?: Blob;
  error?: Error;
}

export interface BatchOptions {
  /** 동시 서버 요청 수. 서버 세마포어와 짝을 맞춘다. 기본 2. */
  concurrency?: number;
  /** 기본은 스티커용 1024 webp (설계문서 C-4). */
  exportOptions?: ExportOptions;
  signal?: AbortSignal;
  onProgress?: (p: { done: number; total: number }) => void;
  onItem?: (r: BatchResult) => void;
}

export interface BatchDeps {
  backend: MaskBackend;
  surfaces: SurfaceFactory;
}

/** 원본 크기와 해상도 지정으로 목표 크기를 구한다. */
export function resolveSize(
  ow: number,
  oh: number,
  resolution: Resolution,
  working?: { w: number; h: number },
): { w: number; h: number } {
  if (resolution === 'original') return { w: ow, h: oh };
  if (resolution === 'working') return working ?? { w: ow, h: oh };
  const s = Math.min(1, resolution / Math.max(ow, oh));
  return { w: Math.max(1, Math.round(ow * s)), h: Math.max(1, Math.round(oh * s)) };
}

/** 한 장을 자동 누끼해서 인코딩한다. 편집 상태 없음. */
export async function autoCutoutOne(
  blob: Blob,
  { backend, surfaces }: BatchDeps,
  opts: ExportOptions & { signal?: AbortSignal } = {},
): Promise<Blob> {
  const { resolution = 1024, format = 'webp', quality = 0.9, signal } = opts;

  const [img, maskImg] = await Promise.all([
    surfaces.decode(blob),
    backend.getMask(blob, signal ? { signal } : {}),
  ]);

  const { w, h } = resolveSize(img.width, img.height, resolution);
  const out = surfaces.create(w, h);
  try {
    out.drawImage(img, 'srcOver', { w, h });
    out.drawImage(maskImg, 'dstIn', { w, h });
    return await out.encode(format, quality);
  } finally {
    out.dispose();
    img.close?.();
    maskImg.close?.();
  }
}

/**
 * 여러 장을 동시성 제한 아래 처리한다. 결과는 **입력 순서**로 돌아온다.
 * 개별 실패는 전체를 중단시키지 않는다 -- 사진 한 장이 깨졌다고
 * 나머지 네 장을 버릴 이유가 없다.
 */
export async function batchAutoCutout(
  blobs: readonly Blob[],
  deps: BatchDeps,
  opts: BatchOptions = {},
): Promise<BatchResult[]> {
  const {
    concurrency = 2,
    exportOptions = { resolution: 1024, format: 'webp', quality: 0.9 },
    signal,
    onProgress,
    onItem,
  } = opts;

  const results: BatchResult[] = new Array(blobs.length);
  let next = 0;
  let done = 0;

  const worker = async (): Promise<void> => {
    for (;;) {
      const i = next++;
      if (i >= blobs.length) return;
      if (signal?.aborted) {
        results[i] = { index: i, ok: false, error: new Error('취소됨') };
      } else {
        try {
          const blob = await autoCutoutOne(blobs[i]!, deps, { ...exportOptions, ...(signal ? { signal } : {}) });
          results[i] = { index: i, ok: true, blob };
        } catch (e) {
          results[i] = { index: i, ok: false, error: e as Error };
        }
      }
      done++;
      onItem?.(results[i]!);
      onProgress?.({ done, total: blobs.length });
    }
  };

  await Promise.all(
    Array.from({ length: Math.max(1, Math.min(concurrency, blobs.length)) }, worker),
  );
  return results;
}
